/**
 * Keyframe playback (TBL-001).
 *
 * The physics already ran to completion before any of this executes
 * (ARCHITECTURE.md §5.1), so playback is pure interpolation — no stepping, no
 * simulation, no decisions.
 *
 * ⚠️ This module is the ONLY thing allowed to know where the balls will be. Nothing
 * else may read ahead of `tS`: no sound that fires early, no aim guide that knows the
 * ball drops.
 */
import { frameBallX, frameBallY, frameTimeS, type SimResult } from '@potted/sim';

export interface Sampler {
  /**
   * Fill `outX`/`outY` with every ball's position at time `tS`, and `outPotted` with
   * 1 for every ball that is down by then.
   * Before the first frame it clamps to the first; past the last it clamps to the
   * last — it never extrapolates.
   *
   * `outSinceDropS`, if given, gets how long ago each ball dropped: -1 while it is
   * still on the table (never its future drop time — rule 5), and +Infinity for a
   * ball that went down on an earlier shot. The renderer uses it to sink the ball.
   */
  sample(
    tS: number,
    outX: Float64Array,
    outY: Float64Array,
    outPotted: Uint8Array,
    outSinceDropS?: Float64Array,
  ): void;
  /**
   * When playback should stop: the end of the shot, or `tailS` after the last drop
   * if that is later, so a ball potted at the very end still gets to sink.
   */
  endS(tailS: number): number;
  readonly durationS: number;
  readonly ballCount: number;
}

export function createSampler(
  result: Pick<SimResult, 'keyframes' | 'events' | 'finalState'>,
): Sampler {
  const { keyframes } = result;
  const lastFrame = Math.max(0, keyframes.count - 1);
  // Playback is monotonic, so remember where we were instead of searching each frame.
  let cursor = 0;

  /*
   * When each ball drops. A ball goes down at its `pocket` event, not at the start of
   * playback: taking "potted" from `finalState` up front made a ball vanish the moment
   * Shoot was pressed (CLAUDE.md §13/L35). A ball already down before this shot has
   * no event here but is potted at the end, so it is down from the start.
   */
  const dropS = new Float64Array(keyframes.ballCount).fill(Number.POSITIVE_INFINITY);
  for (let ball = 0; ball < keyframes.ballCount; ball++) {
    if (result.finalState[ball]?.potted === true) dropS[ball] = Number.NEGATIVE_INFINITY;
  }
  for (const event of result.events) {
    if (event.type !== 'pocket') continue;
    for (const ball of event.ids) {
      if (ball < dropS.length) dropS[ball] = event.tS;
    }
  }

  let lastDropS = Number.NEGATIVE_INFINITY;
  for (const dropped of dropS) {
    if (Number.isFinite(dropped)) lastDropS = Math.max(lastDropS, dropped);
  }
  const durationS = frameTimeS(keyframes, lastFrame);

  return {
    durationS,
    endS: (tailS) => Math.max(durationS, lastDropS + tailS),
    ballCount: keyframes.ballCount,

    sample(tS, outX, outY, outPotted, outSinceDropS) {
      if (keyframes.count === 0) return;

      if (tS <= 0) cursor = 0;
      while (cursor < lastFrame && frameTimeS(keyframes, cursor + 1) <= tS) cursor++;
      while (cursor > 0 && frameTimeS(keyframes, cursor) > tS) cursor--;

      const next = Math.min(cursor + 1, lastFrame);
      const t0 = frameTimeS(keyframes, cursor);
      const t1 = frameTimeS(keyframes, next);
      const span = t1 - t0;
      // Clamped, never extrapolated.
      const alpha = span <= 0 ? 0 : Math.min(1, Math.max(0, (tS - t0) / span));

      for (let ball = 0; ball < keyframes.ballCount; ball++) {
        const x0 = frameBallX(keyframes, cursor, ball);
        const y0 = frameBallY(keyframes, cursor, ball);
        outX[ball] = x0 + (frameBallX(keyframes, next, ball) - x0) * alpha;
        outY[ball] = y0 + (frameBallY(keyframes, next, ball) - y0) * alpha;
        const dropped = dropS[ball] ?? Number.POSITIVE_INFINITY;
        outPotted[ball] = tS >= dropped ? 1 : 0;
        if (outSinceDropS !== undefined) outSinceDropS[ball] = tS >= dropped ? tS - dropped : -1;
      }
    },
  };
}
